import { useCallback, useEffect } from "react";

const SENTINEL_KEY = "attachmentViewer";

const hasViewerSentinel = (state: unknown): boolean =>
  typeof state === "object" &&
  state !== null &&
  (state as Record<string, unknown>)[SENTINEL_KEY] === true;

/**
 * Back-button / edge-swipe support for the attachment viewer: while it is
 * open, a sentinel history entry sits on top of the stack so the platform
 * "back" gesture (Android system Back, iOS Safari edge-swipe, mouse Back)
 * closes the dialog instead of popping the route and leaving the chat.
 *
 * Modeled on BfnCalculatorView's sentinel, with the same two safety rules
 * (both router-driven — BrowserRouter keeps its POP bookkeeping in
 * history.state):
 *  1. the push MERGES into the current entry's state (clobbering it would
 *     break react-router's popstate delta), and
 *  2. the cleanup NEVER traverses — a history.back() there could race an
 *     in-flight navigation (e.g. the close-on-chat-switch path fires DURING a
 *     route change); it only strips the sentinel key in place, leaving a
 *     harmless same-URL entry.
 *
 * The popstate handler only closes when the popped-to entry is NOT the
 * sentinel, which keeps StrictMode's dev double-mount from self-closing.
 *
 * Returns `requestClose` for user-initiated closes (X / Esc / backdrop): it
 * consumes the sentinel entry via history.back() — the resulting popstate
 * completes the close — so a later back press doesn't need two presses.
 */
export function useViewerHistorySentinel(
  open: boolean,
  closeDialog: () => void,
): () => void {
  useEffect(() => {
    if (!open) {
      return;
    }
    window.history.pushState(
      { ...window.history.state, [SENTINEL_KEY]: true },
      "",
    );
    const onPopState = (event: PopStateEvent) => {
      if (!hasViewerSentinel(event.state)) {
        closeDialog();
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // Programmatic close (message deleted, chat switch) or unmount: strip
      // the sentinel in place if it is still on top — never traverse here.
      if (hasViewerSentinel(window.history.state)) {
        const rest = { ...(window.history.state as Record<string, unknown>) };
        delete rest[SENTINEL_KEY];
        window.history.replaceState(rest, "");
      }
    };
  }, [open, closeDialog]);

  return useCallback(() => {
    if (hasViewerSentinel(window.history.state)) {
      window.history.back();
    } else {
      closeDialog();
    }
  }, [closeDialog]);
}
